import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import CommitDrawer from "./CommitDrawer";
import { PermissionGate, type PlaceRef } from "../auth/access";
import type { PremiseCommit } from "../context/PremiseWorkspace";
import type { ActionKey } from "./toast";
import type {
  CallPolicyConfig,
  PremiseConfig,
  ResolvedCallPolicy,
} from "@shared/types/foyer";
import {
  DEFAULT_CALL_ANNOUNCE_SECONDS,
  DEFAULT_CALL_RING_TIMEOUT_SEC,
  resolveCallPolicy,
} from "@shared/types/foyer";
import type { Translate } from "../i18n/translate";

/**
 * Config → Video calls · the premise calling policy (VC-09/VC-12).
 *
 * The three switches save the moment they are flipped (and toast). The quiet
 * hours and timings are a form: they live in an "Edit call rules" drawer whose
 * Save writes them and closes. Either way the whole `care.calls` block is
 * written, so an untouched field can't stay armed from a previous save.
 *
 * Auto-answer keeps its friction: it is a permission to open a camera in a
 * bedroom without anyone pressing accept, so arming it opens a confirmation
 * that requires a separate acknowledgement tick. Turning it OFF is instant.
 */

interface Props {
  premise: PremiseConfig;
  /** Persist one change now and toast (PremiseWorkspace `commit`). */
  onCommit: PremiseCommit;
}

/** Editable mirror of the policy. Numbers live as strings while being typed. */
interface Draft {
  enabled: boolean;
  residentsMayCallOut: boolean;
  allowAutoAnswer: boolean;
  quietFrom: string;
  quietTo: string;
  quietTz: string;
  ringTimeoutSec: string;
  maxCallDurationMin: string;
  announceSeconds: string;
}

function toDraft(p: ResolvedCallPolicy, fallbackTz: string): Draft {
  return {
    enabled: p.enabled,
    residentsMayCallOut: p.residentsMayCallOut,
    allowAutoAnswer: p.allowAutoAnswer,
    quietFrom: p.quietHours?.from ?? "",
    quietTo: p.quietHours?.to ?? "",
    quietTz: p.quietHours?.tz ?? fallbackTz,
    ringTimeoutSec: String(p.ringTimeoutSec),
    maxCallDurationMin:
      p.maxCallDurationMin === null ? "" : String(p.maxCallDurationMin),
    announceSeconds: String(p.announceSeconds),
  };
}

/** The draft as a full policy block, ready to store in `care.calls`. Sending
 *  the whole block (not a patch) means an untouched field can't stay armed from
 *  a previous save. */
function draftToConfig(d: Draft): CallPolicyConfig {
  const int = (v: string, fallback: number) => {
    const n = Number(v);
    return v.trim() && Number.isFinite(n) ? Math.trunc(n) : fallback;
  };
  const quiet =
    d.quietFrom.trim() && d.quietTo.trim()
      ? { from: d.quietFrom.trim(), to: d.quietTo.trim(), tz: d.quietTz.trim() }
      : null;
  return {
    enabled: d.enabled,
    residentsMayCallOut: d.residentsMayCallOut,
    allowAutoAnswer: d.allowAutoAnswer,
    quietHours: quiet,
    ringTimeoutSec: int(d.ringTimeoutSec, DEFAULT_CALL_RING_TIMEOUT_SEC),
    maxCallDurationMin: d.maxCallDurationMin.trim()
      ? int(d.maxCallDurationMin, 0)
      : null,
    announceSeconds: int(d.announceSeconds, DEFAULT_CALL_ANNOUNCE_SECONDS),
  };
}

/** Field-level complaints, keyed by field. Empty = clean. */
function validate(t: Translate, d: Draft): Record<string, string> {
  const errs: Record<string, string> = {};
  const partialQuiet =
    [d.quietFrom, d.quietTo].filter((v) => v.trim()).length === 1;
  if (partialQuiet) {
    errs.quiet = t("care:callPolicyEditor.validation.quietBoth");
  } else if (d.quietFrom.trim() && !d.quietTz.trim()) {
    errs.quiet = t("care:callPolicyEditor.validation.quietTz");
  }
  const ring = Number(d.ringTimeoutSec);
  if (!Number.isInteger(ring) || ring < 5 || ring > 300) {
    errs.ringTimeoutSec = t("care:callPolicyEditor.validation.ring");
  }
  const announce = Number(d.announceSeconds);
  if (!Number.isInteger(announce) || announce < 0 || announce > 60) {
    errs.announceSeconds = t("care:callPolicyEditor.validation.announce");
  }
  if (d.maxCallDurationMin.trim()) {
    const max = Number(d.maxCallDurationMin);
    if (!Number.isInteger(max) || max < 1 || max > 24 * 60) {
      errs.maxCallDurationMin = t(
        "care:callPolicyEditor.validation.maxDuration"
      );
    }
  }
  return errs;
}

/** The calling policy a premise config resolves to, as the editable draft. */
function draftOf(c: PremiseConfig, fallbackTz: string): Draft {
  return toDraft(resolveCallPolicy(c.care), fallbackTz);
}

/** `c` with the draft's patch applied to its stored policy block. */
function withPolicy(
  c: PremiseConfig,
  fallbackTz: string,
  patch: Partial<Draft>
): PremiseConfig {
  return {
    ...c,
    care: {
      ...(c.care ?? {}),
      calls: draftToConfig({ ...draftOf(c, fallbackTz), ...patch }),
    },
  };
}

export default function CallPolicyEditor({ premise, onCommit }: Props) {
  const { t } = useTranslation(["care", "common"]);
  const isSeniorLiving = premise.type === "SENIOR_LIVING";
  const fallbackTz = premise.address?.timezone ?? "Europe/Stockholm";
  const name = premise.premiseName;
  const place: PlaceRef = { premiseId: premise.premiseId };

  // Read straight from the saved premise: each change saves at once and the
  // premise prop comes back updated, so there is no second copy to drift.
  const policy = resolveCallPolicy(premise.care);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [editingRules, setEditingRules] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const toggle = (
    patch: Partial<Draft>,
    success: string,
    failure: ActionKey
  ): Promise<boolean> =>
    onCommit((c) => withPolicy(c, fallbackTz, patch), {
      success,
      failure,
      failureParams: { name },
    });

  /**
   * The switch never flips straight to on: arming opens the confirmation and
   * changes nothing until it is acknowledged. Disarming is immediate.
   */
  const onAutoAnswerToggle = (nextOn: boolean) => {
    if (!nextOn) {
      void toggle(
        { allowAutoAnswer: false },
        t("callPolicyEditor.autoAnswerOff", { name }),
        "turnOffStaffAutoAnswer"
      );
      return;
    }
    setAcknowledged(false);
    setConfirmOpen(true);
  };

  const confirmAutoAnswer = async () => {
    if (!acknowledged) return;
    setConfirming(true);
    const ok = await toggle(
      { allowAutoAnswer: true },
      t("callPolicyEditor.autoAnswerOn", { name }),
      "turnOnStaffAutoAnswer"
    );
    setConfirming(false);
    if (ok) setConfirmOpen(false);
  };

  const quiet = policy.quietHours
    ? t("callPolicyEditor.quietRange", {
        from: policy.quietHours.from,
        to: policy.quietHours.to,
        tz: policy.quietHours.tz,
      })
    : t("callPolicyEditor.quietNone");

  return (
    <Stack spacing={3} data-testid="call-policy">
      {/* ── Master switch ───────────────────────────────────────────────── */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t("callPolicyEditor.title")}
          </Typography>
          <PermissionGate
            resource="video-calls"
            level="administrate"
            place={place}
            deniedProps={{ disabled: true }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={policy.enabled}
                  onChange={(e) =>
                    void toggle(
                      { enabled: e.target.checked },
                      e.target.checked
                        ? t("callPolicyEditor.videoCallingOn", { name })
                        : t("callPolicyEditor.videoCallingOff", { name }),
                      e.target.checked
                        ? "turnOnVideoCallingFor"
                        : "turnOffVideoCallingFor"
                    )
                  }
                  inputProps={
                    { "data-testid": "policy-enabled" } as Record<
                      string,
                      string
                    >
                  }
                />
              }
              label={t("callPolicyEditor.enabled")}
            />
          </PermissionGate>
          {/* VC2-8: there used to be a second, template-side switch for the
              tile, and "policy on, no tile" was the result. Say plainly what
              this switch now decides, so nobody goes looking for the other. */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5, ml: 1.75 }}
            data-testid="policy-enabled-help"
          >
            {t("callPolicyEditor.enabledHelp")}
          </Typography>
        </CardContent>
      </Card>

      {/* ── Call rules - only when calling is on ─────────────────────────── */}
      {policy.enabled && (
        <Card variant="outlined" data-testid="call-rules-card">
          <CardContent>
            <Stack spacing={2.5}>
              <Typography variant="h6">
                {t("callPolicyEditor.callRules")}
              </Typography>

              <PermissionGate
                resource="video-calls"
                level="administrate"
                place={place}
                deniedProps={{ disabled: true }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={policy.residentsMayCallOut}
                      onChange={(e) =>
                        void toggle(
                          { residentsMayCallOut: e.target.checked },
                          e.target.checked
                            ? t("callPolicyEditor.mayCallOut", { name })
                            : t("callPolicyEditor.mayNotCallOut", { name }),
                          "changeResidentsMayCallOut"
                        )
                      }
                      inputProps={
                        { "data-testid": "policy-call-out" } as Record<
                          string,
                          string
                        >
                      }
                    />
                  }
                  label={t("callPolicyEditor.residentsMayCallOut")}
                />
              </PermissionGate>

              <PermissionGate
                resource="video-calls"
                level="administrate"
                place={place}
                deniedProps={{ disabled: true }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      color="warning"
                      checked={policy.allowAutoAnswer}
                      onChange={(e) => onAutoAnswerToggle(e.target.checked)}
                      inputProps={
                        { "data-testid": "policy-auto-answer" } as Record<
                          string,
                          string
                        >
                      }
                    />
                  }
                  label={t("callPolicyEditor.allowAutoAnswer")}
                />
              </PermissionGate>

              {/* ── Quiet hours and timings: a form, so a drawer ─────────── */}
              <Box>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="subtitle2">
                    {t("callPolicyEditor.quietAndTimings")}
                  </Typography>
                  <PermissionGate
                    resource="video-calls"
                    level="administrate"
                    place={place}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setEditingRules(true)}
                      data-testid="policy-edit-rules"
                    >
                      {t("callPolicyEditor.editRules")}
                    </Button>
                  </PermissionGate>
                </Stack>
                <Typography variant="body2" data-testid="policy-rules-summary">
                  {t("callPolicyEditor.summary", {
                    quiet,
                    ring: policy.ringTimeoutSec,
                    max:
                      policy.maxCallDurationMin === null
                        ? t("callPolicyEditor.maxNoLimit")
                        : t("callPolicyEditor.maxMinutes", {
                            count: policy.maxCallDurationMin,
                          }),
                    announce: policy.announceSeconds,
                  })}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {editingRules && (
        <CallRulesDrawer
          premise={premise}
          fallbackTz={fallbackTz}
          onClose={() => setEditingRules(false)}
          onCommit={onCommit}
        />
      )}

      {/* ── Arming friction ─────────────────────────────────────────────── */}
      <Dialog
        open={confirmOpen}
        onClose={() => (confirming ? undefined : setConfirmOpen(false))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: "error.main" }}>
          {t("callPolicyEditor.confirm.title")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            <Trans
              t={t}
              i18nKey="callPolicyEditor.confirm.body"
              values={{ name, seconds: policy.announceSeconds }}
              components={{ em: <em />, strong: <strong /> }}
            />
          </Typography>
          {isSeniorLiving && (
            <Alert
              severity="error"
              sx={{ mb: 1.5 }}
              data-testid="confirm-senior-warning"
            >
              <Trans
                t={t}
                i18nKey="callPolicyEditor.confirm.seniorWarning"
                components={{ strong: <strong /> }}
              />
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("callPolicyEditor.confirm.inert")}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                inputProps={
                  { "data-testid": "confirm-auto-answer-ack" } as Record<
                    string,
                    string
                  >
                }
              />
            }
            label={t("callPolicyEditor.confirm.acknowledge")}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={confirming}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={!acknowledged || confirming}
            onClick={() => void confirmAutoAnswer()}
            data-testid="confirm-auto-answer"
          >
            {confirming
              ? t("common:states.saving")
              : t("callPolicyEditor.confirm.allow")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

/** Quiet hours and the three timings, edited together and saved together. */
function CallRulesDrawer({
  premise,
  fallbackTz,
  onClose,
  onCommit,
}: {
  premise: PremiseConfig;
  fallbackTz: string;
  onClose: () => void;
  onCommit: PremiseCommit;
}) {
  const { t } = useTranslation(["care", "common"]);
  const [draft, setDraft] = useState<Draft>(() => draftOf(premise, fallbackTz));
  const errors = useMemo(() => validate(t, draft), [t, draft]);
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  return (
    <CommitDrawer
      title={t("callPolicyEditor.editRules")}
      subtitle={premise.premiseName}
      onClose={onClose}
      saveDisabled={Object.keys(errors).length > 0}
      data-testid="call-rules-drawer"
      onSave={() =>
        onCommit(
          (c) =>
            withPolicy(c, fallbackTz, {
              quietFrom: draft.quietFrom,
              quietTo: draft.quietTo,
              quietTz: draft.quietTz,
              ringTimeoutSec: draft.ringTimeoutSec,
              maxCallDurationMin: draft.maxCallDurationMin,
              announceSeconds: draft.announceSeconds,
            }),
          {
            success: t("callPolicyEditor.rulesSaved", {
              name: premise.premiseName,
            }),
            failure: "saveCallRulesFor",
            failureParams: { name: premise.premiseName },
          }
        )
      }
    >
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t("callPolicyEditor.drawer.quietHours")}
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              size="small"
              type="time"
              label={t("callPolicyEditor.drawer.from")}
              value={draft.quietFrom}
              onChange={(e) => set({ quietFrom: e.target.value })}
              InputLabelProps={{ shrink: true }}
              inputProps={{ "data-testid": "policy-quiet-from" }}
            />
            <TextField
              size="small"
              type="time"
              label={t("callPolicyEditor.drawer.to")}
              value={draft.quietTo}
              onChange={(e) => set({ quietTo: e.target.value })}
              InputLabelProps={{ shrink: true }}
              inputProps={{ "data-testid": "policy-quiet-to" }}
            />
            <TextField
              size="small"
              label={t("callPolicyEditor.drawer.timezone")}
              value={draft.quietTz}
              onChange={(e) => set({ quietTz: e.target.value })}
              inputProps={{ "data-testid": "policy-quiet-tz" }}
            />
          </Stack>
          {errors.quiet && (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: "block", mt: 1 }}
              data-testid="policy-quiet-help"
            >
              {errors.quiet}
            </Typography>
          )}
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            size="small"
            type="number"
            label={t("callPolicyEditor.drawer.ringTimeout")}
            value={draft.ringTimeoutSec}
            onChange={(e) => set({ ringTimeoutSec: e.target.value })}
            error={!!errors.ringTimeoutSec}
            helperText={errors.ringTimeoutSec}
            inputProps={{ "data-testid": "policy-ring-timeout" }}
          />
          <TextField
            size="small"
            type="number"
            label={t("callPolicyEditor.drawer.maxLength")}
            value={draft.maxCallDurationMin}
            onChange={(e) => set({ maxCallDurationMin: e.target.value })}
            error={!!errors.maxCallDurationMin}
            helperText={errors.maxCallDurationMin}
            inputProps={{ "data-testid": "policy-max-duration" }}
          />
          <TextField
            size="small"
            type="number"
            label={t("callPolicyEditor.drawer.announcement")}
            value={draft.announceSeconds}
            onChange={(e) => set({ announceSeconds: e.target.value })}
            error={!!errors.announceSeconds}
            helperText={errors.announceSeconds}
            inputProps={{ "data-testid": "policy-announce" }}
          />
        </Stack>
      </Stack>
    </CommitDrawer>
  );
}
